import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  FormControl, FormErrorMessage,
  Grid, GridItem,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import React, { useContext, useEffect, forwardRef, useImperativeHandle, useRef, useState } from "react";
import { v4 } from 'uuid';
import { useTranslation } from "react-i18next";
import { FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { isValidDateFormat, isValidDateRange, formatDateByLocale } from "../../utils/utils";
import { groupByYear, calcMonthList } from "../../utils/scheduleUtils";

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import ScheduleTable from './ScheduleTable';

import { Messages, ReviewPhasePresets, CustomerSchedulePresets, FacilityItemPresets, ValidDateRangeEnd, ValidDateRangeStart, WorkflowId } from "../../contexts/constants";
import type {
  ConfirmorInfo,
  CustomerProduct,
  FacilityItem,
  PagePermissions,
  ProjectContextType,
  ProjectWithRdsData,
  ReviewListItem,
  ReviewScheduleItem,
  ScheduleItem,
} from '../../contexts/types';
import { useAppSelector } from "../../store/hooks";
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import ConfirmorList from "./ConfirmorList";

// コンテキスト
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

export interface SchedulingHandle {
  getSnapshot: () => Record<string, unknown>;
  applySnapshot: (data: Record<string, unknown>) => void;
}

/**
 * プロパティ
 */
interface SchedulingProps {
  projectId: string;
  reviewList?: ReviewListItem[];
}

// Confirmor型を定義
interface Confirmor {
  scheduling?: {
    companyDivision?: string[];
    salesDivision?: string[];
  };
}

/**
 * 並び替え機能を提供するカスタムフック
 * 複数の連動する配列（items, tempItems, inputModes）を一括で並び替える
 */
function useReorderableList(
  syncedArrays: {
    get: () => any[];
    set: React.Dispatch<React.SetStateAction<any[]>>;
  }[]
) {
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const toggleReorderMode = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setIsReorderMode(prev => !prev);
    setDraggedIndex(null);
    setTimeout(() => setIsProcessing(false), 300);
  };

  const dragStart = (index: number) => setDraggedIndex(index);
  const dragOver = (e: React.DragEvent) => e.preventDefault();
  const dragEnd = () => setDraggedIndex(null);

  const drop = (targetIndex: number) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;
    syncedArrays.forEach(({ get, set }) => {
      const arr = [...get()];
      const [removed] = arr.splice(draggedIndex, 1);
      arr.splice(targetIndex, 0, removed);
      set(arr);
    });
    setDraggedIndex(null);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    syncedArrays.forEach(({ get, set }) => {
      const arr = [...get()];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      set(arr);
    });
  };

  const moveDown = (index: number) => {
    const length = syncedArrays[0]?.get().length ?? 0;
    if (index >= length - 1) return;
    syncedArrays.forEach(({ get, set }) => {
      const arr = [...get()];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      set(arr);
    });
  };

  return {
    isReorderMode,
    draggedIndex,
    isProcessing,
    toggleReorderMode,
    dragStart,
    dragOver,
    dragEnd,
    drop,
    moveUp,
    moveDown,
  };
}

const Scheduling = forwardRef<SchedulingHandle, SchedulingProps>(({ projectId, reviewList = [] }, ref) => {

  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  /** 画面タイトル */
  const TITLE = t('features.InvestmentScreening.Scheduling.title');

  // フィールド名（翻訳済み、エラー文字列のフィルタ判定で使用）
  const FIELD_ITEM_NAME_LABEL = t('features.InvestmentScreening.Scheduling.field.itemName');
  const FIELD_DATE_LABEL = t('features.InvestmentScreening.Scheduling.field.date');
  const FIELD_START_DATE_LABEL = t('features.InvestmentScreening.Scheduling.field.startDate');
  const FIELD_END_DATE_LABEL = t('features.InvestmentScreening.Scheduling.field.endDate');
  const FIELD_PHASE_LABEL = t('features.InvestmentScreening.Scheduling.field.phase');
  const FIELD_REVIEW_DATE_LABEL = t('features.InvestmentScreening.Scheduling.field.reviewDateField');

  const employeeId = useAppSelector((state) => state.employee.employeeId);
  const divisionId = useAppSelector((state) => state.employee.divisionId);

  // -----------------------------
  // useRef
  // -----------------------------
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    companyHeadDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };

  // -----------------------------
  // useState
  // -----------------------------
  // Projectデータ
  const [projectData, setProjectData] = useState<ProjectWithRdsData>();
  // 顧客スケジュール (単一日付)
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([{ id: `schedule-${v4()}`, name: "", date: "" }]);
  // 顧客スケジュール (単一日付) 入力チェック一時保存用
  const [tempScheduleItems, setTempScheduleItems] = useState<ScheduleItem[]>([{ id: `schedule-${v4()}`, name: "", date: "" }]);
  // 設備投資スケジュール (開始日・終了日)
  const [facilityItems, setFacilityItems] = useState<FacilityItem[]>([{ id: `facility-${v4()}`, name: "", startDate: "", endDate: "" }]);
  // 設備投資スケジュール (開始日・終了日) 入力チェック一時保存用
  const [tempFacilityItems, setTempFacilityItems] = useState<FacilityItem[]>([{ id: `facility-${v4()}`, name: "", startDate: "", endDate: "" }]);
  // 横軸に表示する "YYYY-MM" の一覧 (顧客スケジュール + 設備投資スケジュール の最小～最大を1か月拡張)
  const [monthList, setMonthList] = useState<string[]>([]);
  // 顧客製品情報
  const [customerProductData, setCustomerProductData] = useState<CustomerProduct>();
  // エラー情報
  const [scheduleErrors, setScheduleErrors] = useState<string[][]>([]);
  const [facilityErrors, setFacilityErrors] = useState<string[][]>([]);
  // どの部署の確認かを表す
  const [confirmSource, setConfirmSource] = useState<string>("");
  // 確認担当者
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);
  // 確認ボタン横幅
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});
  // 確認ボタン下担当者名
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    salesDivision: [],
  });
  // 顧客スケジュール 各行の入力モード（"select" | "input"）
  const [scheduleInputModes, setScheduleInputModes] = useState<("select" | "input")[]>(["select"]);
  // 設備投資スケジュール 各行の入力モード
  const [facilityInputModes, setFacilityInputModes] = useState<("select" | "input")[]>(["select"]);
  // 振り返りスケジュール
  const [reviewItems, setReviewItems] = useState<ReviewScheduleItem[]>([]);
  // 振り返りスケジュール 入力チェック一時保存用
  const [tempReviewItems, setTempReviewItems] = useState<ReviewScheduleItem[]>([]);
  // 振り返りスケジュール エラー情報
  const [reviewErrors, setReviewErrors] = useState<string[][]>([]);
  // 振り返りスケジュール 各行の入力モード（"select" | "input"）
  const [reviewInputModes, setReviewInputModes] = useState<("select" | "input")[]>([]);
  // 振り返り済みかどうか判定（データ読み込み時に付与されたフラグで判定）
  const isReviewedItem = (index: number): boolean => {
    return !!reviewItems[index]?.isReviewed;
  };
  // 営業支援スケジュール取得中フラグ
  const [isFetchingSasSchedule, setIsFetchingSasSchedule] = useState(false);
  // 並び替え（顧客スケジュール）
  const scheduleReorder = useReorderableList([
    { get: () => scheduleItems, set: setScheduleItems as React.Dispatch<React.SetStateAction<any[]>> },
    { get: () => tempScheduleItems, set: setTempScheduleItems as React.Dispatch<React.SetStateAction<any[]>> },
    { get: () => scheduleInputModes, set: setScheduleInputModes as React.Dispatch<React.SetStateAction<any[]>> },
  ]);
  // 並び替え（設備投資スケジュール）
  const facilityReorder = useReorderableList([
    { get: () => facilityItems, set: setFacilityItems as React.Dispatch<React.SetStateAction<any[]>> },
    { get: () => tempFacilityItems, set: setTempFacilityItems as React.Dispatch<React.SetStateAction<any[]>> },
    { get: () => facilityInputModes, set: setFacilityInputModes as React.Dispatch<React.SetStateAction<any[]>> },
  ]);
  // 並び替え（振り返りスケジュール）
  const reviewReorder = useReorderableList([
    { get: () => reviewItems, set: setReviewItems as React.Dispatch<React.SetStateAction<any[]>> },
    { get: () => tempReviewItems, set: setTempReviewItems as React.Dispatch<React.SetStateAction<any[]>> },
    { get: () => reviewInputModes, set: setReviewInputModes as React.Dispatch<React.SetStateAction<any[]>> },
  ]);

  /** 権限用 画面パーミッション */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();

  // -----------------------------
  // 権限情報
  // -----------------------------
  const { userPermissions, reviewDate } = useContext(ProjectContext) as ProjectContextType;

  // 一時保存対象（6項目）: scheduleItems / scheduleInputModes / facilityItems /
  //   facilityInputModes / reviewItems / reviewInputModes
  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const data: Record<string, unknown> = {};
      if (scheduleItems.length > 0) data.scheduleItems = scheduleItems;
      if (scheduleInputModes.length > 0) data.scheduleInputModes = scheduleInputModes;
      if (facilityItems.length > 0) data.facilityItems = facilityItems;
      if (facilityInputModes.length > 0) data.facilityInputModes = facilityInputModes;
      if (reviewItems.length > 0) data.reviewItems = reviewItems;
      if (reviewInputModes.length > 0) data.reviewInputModes = reviewInputModes;
      return data;
    },
    applySnapshot: (data: Record<string, unknown>) => {
      if (data.scheduleItems !== undefined) setScheduleItems(data.scheduleItems as ScheduleItem[]);
      if (data.scheduleInputModes !== undefined) setScheduleInputModes(data.scheduleInputModes as ("select" | "input")[]);
      if (data.facilityItems !== undefined) setFacilityItems(data.facilityItems as FacilityItem[]);
      if (data.facilityInputModes !== undefined) setFacilityInputModes(data.facilityInputModes as ("select" | "input")[]);
      if (data.reviewItems !== undefined) setReviewItems(data.reviewItems as ReviewScheduleItem[]);
      if (data.reviewInputModes !== undefined) setReviewInputModes(data.reviewInputModes as ("select" | "input")[]);
    },
  }));

  // -----------------------------
  // useDisclosure
  // -----------------------------
  const { isOpen, onOpen, onClose } = useDisclosure(); // Modal の開閉状態を管理

  // -----------------------------
  // useEffect
  // -----------------------------
  /**
   * 権限情報
   */
  useEffect(() => {
    const pagePermissions = getPermissions(userPermissions, WorkflowId.Scheduling, projectData?.purpose, employeeId, divisionId, projectData?.registrantDivisionId);
    setPagePermissions(pagePermissions);
  }, [userPermissions, employeeId, divisionId, projectData]);

  /** 画面初期表示時のデータ取得 */
  useEffect(() => {
    console.log("Scheduling start");
    getDispData();
  }, []);

  // 顧客スケジュール一時テーブル
  useEffect(() => {
    const scheduleItemsCopy = scheduleItems.map(item => ({ ...item }));
    setTempScheduleItems(scheduleItemsCopy);
  }, [scheduleItems]);

  // 設備投資スケジュール一時テーブル
  useEffect(() => {
    const facilityItemsCopy = facilityItems.map(item => ({ ...item }));
    setTempFacilityItems(facilityItemsCopy);
  }, [facilityItems]);

  // 振り返りスケジュール一時テーブル
  useEffect(() => {
    const reviewItemsCopy = reviewItems.map(item => ({ ...item }));
    setTempReviewItems(reviewItemsCopy);
  }, [reviewItems]);

  // 顧客スケジュール & 設備投資スケジュールの最小日付・最大日付を取得し、
  // そこから1か月前・1か月後を算出して monthList を更新
  useEffect(() => {
    setMonthList(calcMonthList(scheduleItems, facilityItems, reviewItems));
  }, [scheduleItems, facilityItems, reviewItems]);

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    const fetchConfirmor = async () => {
      try {
        // 確認担当者JSONを取得
        const confirmorData = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmorData);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }
    };
    if (projectId) {
      fetchConfirmor();
    }
  }, [projectId, reviewDate]);

  useEffect(() => {
    if (confirmor?.scheduling) {
      const updatedNames = Object.keys(names).reduce((acc, division) => {
        const divisionData = confirmor.scheduling?.[division as keyof Confirmor['scheduling']];
        if (Array.isArray(divisionData)) {
          acc[division] = divisionData;
        } else {
          acc[division] = [];
        }
        return acc;
      }, {} as Record<string, ConfirmorInfo[]>);
      setNames(updatedNames);
    }
  }, [confirmor]);

  // -----------------------------
  // API
  // -----------------------------
  // API実行
  const getDispData = async () => {
    try {
      if (projectId && projectId !== "") {

        const projectResult = await getProjectData();

      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Scheduling.message.registerFromBasicInfo'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('getDispData:', error);
      toast({
        title: t('common.label.failed'),
        description: t('common.api.commonError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /** プロジェクト情報取得API */
  const getProjectData = async () => {
    try {
      console.log(`getProjectData():start`);

      const payload = {
        query: `
          query getProjectData($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
            getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
              projectId
              revisedOpportunityNameByRevision
              customerSchedule
              equipmentInstallationSchedule
              reviewSchedule
              projectStartDate
              projectEndDate
              customerProductId
              purpose
              createDate
              updateDate
              # RDS Data
              opportunityId
              opportunityName
              registrantDivisionId
              updateDateRDS
            }
          }`,
        variables: {
          projectId: projectId,
          useRDSData: true,
          ...(reviewDate && { reviewDate })
        }
      };

      // プロジェクト情報取得（dynamodb、RDS）
      const response = await WebAPI.request(payload);

      // データ取得に成功した場合
      if (response && response.data && response.data.getProjectById) {
        const projectInfo = response.data.getProjectById;
        const scheduleItems = projectInfo.customerSchedule ? JSON.parse(projectInfo.customerSchedule) : [];
        const facilityItems = projectInfo.equipmentInstallationSchedule ? JSON.parse(projectInfo.equipmentInstallationSchedule) : [];
        const reviewItemsRaw: ReviewScheduleItem[] = projectInfo.reviewSchedule ? JSON.parse(projectInfo.reviewSchedule) : [];

        // 振り返り済みフラグを付与（reviewListの日付と一致する行）
        const reviewedDateSet = new Set(reviewList.map(r => r.reviewDate));
        const reviewItems = reviewItemsRaw.map(item => ({
          ...item,
          isReviewed: reviewedDateSet.has(item.date),
        }));

        setProjectData(projectInfo);
        setScheduleItems(scheduleItems);
        setFacilityItems(facilityItems);
        setReviewItems(reviewItems);

        // 振り返りスケジュール入力モードの初期化
        const reviewModes: ("select" | "input")[] = reviewItems.length > 0
          ? reviewItems.map((item: ReviewScheduleItem) =>
            item.inputMode === "select" && ReviewPhasePresets.some(p => p.value === item.phase) ? "select" : "input"
          )
          : [];
        setReviewInputModes(reviewModes);

        // 入力モードの初期化
        // - JSONのinputModeを使用、未設定の既存データは"input"にフォールバック
        // - "select"でもプリセットに存在しない場合は"input"にフォールバック（プリセット削除対策）
        const resolveScheduleMode = (item: { inputMode?: string; name?: string }): "select" | "input" =>
          item.inputMode === "select" && CustomerSchedulePresets.some(p => p.value === item.name) ? "select" : "input";
        const resolveFacilityMode = (item: { inputMode?: string; name?: string }): "select" | "input" =>
          item.inputMode === "select" && FacilityItemPresets.some(p => p.value === item.name) ? "select" : "input";
        const scheduleModes: ("select" | "input")[] = scheduleItems.length > 0
          ? scheduleItems.map((item: ScheduleItem) => resolveScheduleMode(item))
          : ["select"];
        setScheduleInputModes(scheduleModes);
        const facilityModes: ("select" | "input")[] = facilityItems.length > 0
          ? facilityItems.map((item: FacilityItem) => resolveFacilityMode(item))
          : ["select"];
        setFacilityInputModes(facilityModes);

        // 顧客製品情報取得
        await getCustomerProductData(projectInfo.customerProductId);

      } else {
        // データ取得失敗の場合
        toast({
          title: t('features.InvestmentScreening.Scheduling.message.dataFetchErrorTitle'),
          description: t('features.InvestmentScreening.Scheduling.message.projectFetchFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in getProjectData:", error);
      return false;
    }
  };

  /** 顧客製品情報取得API */
  const getCustomerProductData = async (customerProductId: string) => {
    try {
      console.log(`getCustomerProductData():start`);

      const payload = {
        query: `
          query getCustomerProductByCustomerProductId($customerProductId: String) {
            getCustomerProductByCustomerProductId(customerProductId: $customerProductId) {
              customerProductId
              projectStart
              massProduction
              schedule
            }
          }`,
        variables: { customerProductId }
      };

      // 顧客製品情報取得（dynamodb）
      const response = await WebAPI.request(payload);

      // データ取得に成功した場合
      if (response && response.data && response.data.getCustomerProductByCustomerProductId) {
        const customerProductData: CustomerProduct = response.data.getCustomerProductByCustomerProductId;

        setCustomerProductData(customerProductData);
      } else {
        // 顧客製品名: 自由入力化に伴い、マスタに存在しない場合はエラーを表示せずスキップ
        console.log(`getCustomerProductData(): customerProductId="${customerProductId}" はマスタに存在しないためスキップ`);
        return true;
      }

      return true;
    } catch (error) {
      console.error("Error in getCustomerProductData:", error);
      return false;
    }
  };

  /** 営業支援スケジュール取得API */
  const fetchSasSchedule = async (opportunityId: string) => {
    try {
      console.log(`fetchSasSchedule():start opportunityId=${opportunityId}`);

      const payload = {
        query: `
          query getSasSchedule($opportunityId: String!) {
            getSasSchedule(opportunityId: $opportunityId) {
              scheduleItems {
                name
                date
                source
              }
              found
            }
          }`,
        variables: { opportunityId }
      };

      const response = await WebAPI.request(payload);

      if (response?.data?.getSasSchedule) {
        return response.data.getSasSchedule as {
          scheduleItems: ScheduleItem[];
          found: boolean;
        };
      }
      return { scheduleItems: [], found: false };
    } catch (error) {
      console.error("Error in fetchSasSchedule:", error);
      return { scheduleItems: [], found: false };
    }
  };

  /** 営業支援からスケジュール取得ボタン押下 */
  const handleFetchSasSchedule = async () => {
    const opportunityId = projectData?.opportunityId;
    if (!opportunityId) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Scheduling.message.opportunityIdNotSet'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsFetchingSasSchedule(true);
    try {
      const result = await fetchSasSchedule(opportunityId);

      if (!result.found || result.scheduleItems.length === 0) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Scheduling.message.sasNotFound'),
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // 既存データを保持し、営業支援データ(source='sas')のみ置き換え
      // sourceが設定されている行（設備投資等）は空でも保持、source未設定の空行のみ除外
      const keepItem = (item?: ScheduleItem) => item != null && item.source !== 'sas' && (item.source || item.name || item.date);
      const ceasItems = scheduleItems.filter(keepItem);
      const ceasModes = scheduleInputModes.filter((_, i) => keepItem(scheduleItems[i]));
      // 営業支援データは「入力」モードで追加
      const sasItems: ScheduleItem[] = result.scheduleItems.map(item => ({
        ...item,
        id: item.id || `schedule-${v4()}`,
        source: 'sas' as const,
      }));
      const sasModes: ("select" | "input")[] = sasItems.map(() => "input");

      const newItems = [...ceasItems, ...sasItems];
      const newModes = [...ceasModes, ...sasModes];

      setScheduleItems(newItems);
      setScheduleInputModes(newModes);

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Scheduling.message.sasFetchSuccess', { count: sasItems.length }),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error in handleFetchSasSchedule:", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Scheduling.message.sasFetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsFetchingSasSchedule(false);
    }
  };

  /** スケジュール更新API */
  const updateSchedules = async () => {
    try {
      const scheduleItemsWithMode = scheduleItems.map((item, i) => ({
        ...item,
        inputMode: (scheduleInputModes[i] ?? "input") as "select" | "input",
      }));
      const facilityItemsWithMode = facilityItems.map((item, i) => ({
        ...item,
        inputMode: (facilityInputModes[i] ?? "input") as "select" | "input",
      }));
      const scheduleItemsJson = JSON.stringify(scheduleItemsWithMode);
      const facilityItemsJson = JSON.stringify(facilityItemsWithMode);
      const reviewItemsWithMode = reviewItems.map((item, i) => {
        const { isReviewed, ...rest } = item; // isReviewedはUI制御用のため保存対象外
        return {
          ...rest,
          inputMode: (reviewInputModes[i] ?? "input") as "select" | "input",
        };
      });
      const reviewItemsJson = JSON.stringify(reviewItemsWithMode);
      const input = {
        projectId: projectId,
        customerSchedule: scheduleItemsJson,
        equipmentInstallationSchedule: facilityItemsJson,
        reviewSchedule: reviewItemsJson,
        updateDate: projectData?.updateDate,
        ...(reviewDate && { reviewDate }),
      };

      // DB更新
      const query = `
        mutation updateSchedule ($input: ScheduleInput!) {
          updateSchedule(input: $input) {status}
        }
      `;
      const result = await WebAPI.request({
        query: query,
        variables: { input }
      });
      if (result?.data?.updateSchedule.status === 'success') {
        toast({
          title: t('common.label.success'),
          description: t('features.InvestmentScreening.Scheduling.message.saveSuccess'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        console.log("updateSchedules successfully");

        // 再取得
        await getProjectData();
      } else if (result?.data?.updateSchedule.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. updateSchedule.`);
      } else if (result?.data?.updateSchedule.status === 'review_conflict') {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.Scheduling.message.reviewConflict'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Review conflict error. updateSchedule.`);
      } else {
        throw new Error(result);
      }
    } catch (error) {
      console.error("Error updating schedules:", error);
      if (error instanceof Error) {
        throw new Error(error.message); // Error オブジェクトの場合はそのメッセージを使用
      } else {
        throw new Error(String(error)); // それ以外の場合は文字列に変換
      }
    }
  };

  // -----------------------------
  // イベント
  // -----------------------------
  /** 顧客スケジュール 追加 */
  const handleAddScheduleItem: React.MouseEventHandler<HTMLButtonElement> = (event) => {

    event.currentTarget.blur();

    const newId = `schedule-${v4()}`;

    setScheduleItems([...scheduleItems, { id: newId, name: "", date: "" }]);
    setTempScheduleItems([...tempScheduleItems, { id: newId, name: "", date: "" }]);
    setScheduleInputModes([...scheduleInputModes, "select"]);
  };

  /** 顧客スケジュール 削除 */
  const handleRemoveScheduleItem = (index: number) => {
    setScheduleItems(scheduleItems.filter((_, i) => i !== index));
    setTempScheduleItems(tempScheduleItems.filter((_, i) => i !== index));
    setScheduleErrors(scheduleErrors.filter((_, i) => i !== index)); // エラーメッセージ初期化
    setScheduleInputModes(scheduleInputModes.filter((_, i) => i !== index));
  };

  /** 顧客スケジュール 項目名入力 */
  const handleScheduleItemNameChange = (index: number, value: string) => {
    const updated = [...tempScheduleItems];
    updated[index]["name"] = value;
    setTempScheduleItems(updated);
  };

  /** 顧客スケジュール 日付入力 */
  const handleScheduleItemChange = (index: number, value: string) => {
    if (value.length > 10) return;

    const updated = [...tempScheduleItems];
    updated[index]["date"] = value;
    setTempScheduleItems(updated);
  };

  /** 顧客スケジュール 項目名フォーカスアウト */
  const handleScheduleItemNameBlur = (index: number) => {
    const updated = [...tempScheduleItems];
    const value = updated[index]["name"];

    // 変更がある場合のみ状態を更新
    if (scheduleItems[index]["name"] !== value) {
      const finalUpdated = [...scheduleItems];
      finalUpdated[index]["name"] = value;
      setScheduleItems(finalUpdated);
    }
  };

  /** 顧客スケジュール 日付フォーカスアウト */
  const handleScheduleItemDateBlur = (index: number) => {
    const updated = [...tempScheduleItems];
    const value = updated[index]["date"];

    // 入力チェック一時保存用(保存時の入力チェックで使用する)
    // 変更がある場合のみ状態を更新
    if (tempScheduleItems[index]["date"] !== value) {
      const finalUpdatedTemp = [...tempScheduleItems];
      finalUpdatedTemp[index]["date"] = value;
      setTempScheduleItems(finalUpdatedTemp);
    }

    // 入力チェック
    // ※値に不備があった場合、画面のスケジュールは更新しない
    if (!isValidDateFormat(value)) {
      // 日付フォーマット不備
      return;
    }
    if (!isValidDateRange(new Date(value))) {
      // 日付が有効範囲外
      return;
    }

    // 変更がある場合のみ状態を更新
    // ※スケジュールの描画更新。不備がない場合のみ実施
    if (scheduleItems[index]["date"] !== value) {
      const finalUpdated = [...scheduleItems];
      finalUpdated[index]["date"] = value;
      setScheduleItems(finalUpdated);
    }
  };

  /** 設備投資スケジュール 追加 */
  const handleAddFacilityItem: React.MouseEventHandler<HTMLButtonElement> = (event) => {

    event.currentTarget.blur();

    const newId = `facility-${v4()}`;
    setFacilityItems([...facilityItems, { id: newId, name: "", startDate: "", endDate: "" }]);
    setTempFacilityItems([...tempFacilityItems, { id: newId, name: "", startDate: "", endDate: "" }]);
    setFacilityInputModes([...facilityInputModes, "select"]);
  };

  /** 設備投資スケジュール 削除 */
  const handleRemoveFacilityItem = (index: number) => {
    setFacilityItems(facilityItems.filter((_, i) => i !== index));
    setTempFacilityItems(tempFacilityItems.filter((_, i) => i !== index));
    setFacilityErrors(facilityErrors.filter((_, i) => i !== index)); // エラーメッセージ初期化
    setFacilityInputModes(facilityInputModes.filter((_, i) => i !== index));
  };

  /** 設備投資スケジュール 項目名入力 */
  const handleFacilityItemNameChange = (index: number, value: string) => {
    const updated = [...tempFacilityItems];
    updated[index]["name"] = value;
    setTempFacilityItems(updated);
  };

  /** 設備投資スケジュール 日付入力 */
  const handleFacilityItemDateChange = (index: number, key: "startDate" | "endDate", value: string) => {
    if (value.length > 10) return;

    const updated = [...tempFacilityItems];
    updated[index][key] = value;
    setTempFacilityItems(updated);
  };

  /** 設備投資スケジュール 項目名フォーカスアウト */
  const handleFacilityItemNameBlur = (index: number) => {
    const updated = [...tempFacilityItems];
    const value = updated[index]["name"];

    // 変更がある場合のみ状態を更新
    if (facilityItems[index]["name"] !== value) {
      const finalUpdated = [...facilityItems];
      finalUpdated[index].name = value;
      setFacilityItems(finalUpdated);
    }
  };

  /** 設備投資スケジュール 日付フォーカスアウト */
  const handleFacilityItemDateBlur = (index: number, key: "startDate" | "endDate") => {
    const updated = [...tempFacilityItems];
    const value = updated[index][key];

    // 変更がある場合のみ状態を更新
    if (tempFacilityItems[index][key] !== value) {
      const finalUpdatedTemp = [...tempFacilityItems];
      finalUpdatedTemp[index][key] = value;
      setTempFacilityItems(finalUpdatedTemp);
    }

    // 入力チェック
    // ※値に不備があった場合、画面のスケジュールは更新しない
    if (!isValidDateFormat(value)) {
      // 日付フォーマット不備
      return;
    }
    if (!isValidDateRange(new Date(value))) {
      // 日付が有効範囲外
      return;
    }

    // 変更がある場合のみ状態を更新
    if (facilityItems[index][key] !== value) {
      const finalUpdated = [...facilityItems];
      finalUpdated[index][key] = value;
      setFacilityItems(finalUpdated);
    }
  };

  /** 振り返りスケジュール 追加 */
  const handleAddReviewItem: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.currentTarget.blur();

    const newId = `review-${v4()}`;
    setReviewItems([...reviewItems, { id: newId, date: "", phase: "" }]);
    setTempReviewItems([...tempReviewItems, { id: newId, date: "", phase: "" }]);
    setReviewInputModes([...reviewInputModes, "select"]);
  };

  /** 振り返りスケジュール 削除 */
  const handleRemoveReviewItem = (index: number) => {
    setReviewItems(reviewItems.filter((_, i) => i !== index));
    setTempReviewItems(tempReviewItems.filter((_, i) => i !== index));
    setReviewErrors(reviewErrors.filter((_, i) => i !== index));
    setReviewInputModes(reviewInputModes.filter((_, i) => i !== index));
  };

  /** 振り返りスケジュール フェーズ選択（Select） */
  const handleReviewPhaseSelectChange = (index: number, value: string) => {
    const updated = [...reviewItems];
    updated[index] = { ...updated[index], phase: value };
    setReviewItems(updated);
    const updatedTemp = [...tempReviewItems];
    updatedTemp[index] = { ...updatedTemp[index], phase: value };
    setTempReviewItems(updatedTemp);
  };

  /** 振り返りスケジュール フェーズ入力（テキスト） */
  const handleReviewPhaseInputChange = (index: number, value: string) => {
    const updated = [...tempReviewItems];
    updated[index] = { ...updated[index], phase: value };
    setTempReviewItems(updated);
  };

  /** 振り返りスケジュール フェーズ入力フォーカスアウト */
  const handleReviewPhaseInputBlur = (index: number) => {
    const value = tempReviewItems[index].phase;
    if (reviewItems[index]?.phase !== value) {
      const finalUpdated = [...reviewItems];
      finalUpdated[index] = { ...finalUpdated[index], phase: value };
      setReviewItems(finalUpdated);
    }
  };

  /** 振り返りスケジュール 入力モード切替 */
  const toggleReviewInputMode = (index: number, mode: "select" | "input") => {
    const newModes: ("select" | "input")[] = [...reviewInputModes];
    newModes[index] = mode;
    setReviewInputModes(newModes);
    // モード切替時にフェーズをクリア
    const updated = [...reviewItems];
    updated[index] = { ...updated[index], phase: "" };
    setReviewItems(updated);
    const updatedTemp = [...tempReviewItems];
    updatedTemp[index] = { ...updatedTemp[index], phase: "" };
    setTempReviewItems(updatedTemp);
  };

  /** 振り返りスケジュール 日付入力 */
  const handleReviewDateChange = (index: number, value: string) => {
    if (value.length > 10) return;
    const updated = [...tempReviewItems];
    updated[index] = { ...updated[index], date: value };
    setTempReviewItems(updated);
  };

  /** 振り返りスケジュール 日付フォーカスアウト */
  const handleReviewDateBlur = (index: number) => {
    const value = tempReviewItems[index].date;

    if (!isValidDateFormat(value) || !isValidDateRange(new Date(value))) {
      return;
    }

    if (reviewItems[index]?.date !== value) {
      const finalUpdated = [...reviewItems];
      finalUpdated[index] = { ...finalUpdated[index], date: value };
      setReviewItems(finalUpdated);
    }
  };

  // ===== 入力モード切替・Select選択ハンドラー =====

  /** 顧客スケジュール 入力モード切替 */
  const toggleScheduleInputMode = (index: number, mode: "select" | "input") => {
    const newModes: ("select" | "input")[] = [...scheduleInputModes];
    newModes[index] = mode;
    setScheduleInputModes(newModes);
    // モード切替時に項目名をクリア
    const updated = [...scheduleItems];
    updated[index] = { ...updated[index], name: "" };
    setScheduleItems(updated);
    const updatedTemp = [...tempScheduleItems];
    updatedTemp[index] = { ...updatedTemp[index], name: "" };
    setTempScheduleItems(updatedTemp);
  };

  /** 設備投資スケジュール 入力モード切替 */
  const toggleFacilityInputMode = (index: number, mode: "select" | "input") => {
    const newModes: ("select" | "input")[] = [...facilityInputModes];
    newModes[index] = mode;
    setFacilityInputModes(newModes);
    // モード切替時に項目名をクリア
    const updated = [...facilityItems];
    updated[index] = { ...updated[index], name: "" };
    setFacilityItems(updated);
    const updatedTemp = [...tempFacilityItems];
    updatedTemp[index] = { ...updatedTemp[index], name: "" };
    setTempFacilityItems(updatedTemp);
  };

  /** 顧客スケジュール Select選択ハンドラー */
  const handleScheduleItemSelectChange = (index: number, value: string) => {
    const updated = [...scheduleItems];
    updated[index] = { ...updated[index], name: value };
    setScheduleItems(updated);
    const updatedTemp = [...tempScheduleItems];
    updatedTemp[index] = { ...updatedTemp[index], name: value };
    setTempScheduleItems(updatedTemp);
  };

  /** 設備投資スケジュール Select選択ハンドラー */
  const handleFacilityItemSelectChange = (index: number, value: string) => {
    const updated = [...facilityItems];
    updated[index] = { ...updated[index], name: value };
    setFacilityItems(updated);
    const updatedTemp = [...tempFacilityItems];
    updatedTemp[index] = { ...updatedTemp[index], name: value };
    setTempFacilityItems(updatedTemp);
  };

  /** 部署確認ボタン押下時 */
  const handleDepartmentConfirm: React.MouseEventHandler<HTMLButtonElement> = (event) => {

    event.currentTarget.blur();

    // 画面間入力チェック
    if (!isRequiredDataEntered()) {
      return;
    }

    // どの部署からの確認かを保持
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);

    if (validateSchedules()) {
      openModal();
    }
  };

  /** 確認モーダル 確認ボタン押下時 */
  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      // フォーカスを外す
      e.currentTarget.blur();
      // DB更新
      await updateSchedules();

      try {
        // ワークフローテーブルを更新
        await updateConfirm(
          WebAPI,
          projectId,
          WorkflowId.Scheduling,
          employeeId,
          confirmSource,
          "true",
          reviewDate,
        );
        // 確認担当者JSONを取得
        const confirmor = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmor);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }

    } catch (error) {
      // 保存失敗時のトースト
      toast({
        title: t('common.label.failed'),
        description: t('common.api.commonError'),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("保存エラー:", error);
    } finally {
      // モーダルを閉じる
      onClose();
    }
  };

  // -----------------------------
  // ロジック
  // -----------------------------
  /** 画面間チェック */
  const isRequiredDataEntered = () => {
    // 基本情報
    if (!projectId || projectId === "") {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Scheduling.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    return true;
  }



  /** 入力チェック */
  const validateSchedules = () => {
    const scheduleErrorsTemp: string[][] = [];
    const facilityErrorsTemp: string[][] = [];

    // フィールド名（翻訳済み、コンポーネントレベルで定義した値を使用）
    const FIELD_ITEM_NAME = FIELD_ITEM_NAME_LABEL;
    const FIELD_DATE = FIELD_DATE_LABEL;
    const FIELD_START_DATE = FIELD_START_DATE_LABEL;
    const FIELD_END_DATE = FIELD_END_DATE_LABEL;
    // エラーメッセージ
    // 必須
    const requiredMessage = t('features.InvestmentScreening.Scheduling.validation.fieldRequiredSuffix');
    // 年月日 - フォーマットメッセージ
    const isValidDateFormatMessage = t('features.InvestmentScreening.Scheduling.validation.dateFormatSuffix');
    // 年月日 - 有効範囲メッセージ
    const inputDateRangeMessage = t('features.InvestmentScreening.Scheduling.validation.dateRangeSuffix', {
      startDate: formatDateByLocale(ValidDateRangeStart),
      endDate: formatDateByLocale(ValidDateRangeEnd),
    });


    // --------------------
    // 顧客スケジュール
    // --------------------
    if (scheduleItems.length === 0) {
      toast({
        title: t('common.label.inputError'),
        description: t('features.InvestmentScreening.Scheduling.validation.customerRequired'),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    // 必須・フォーマットチェック
    // ※入力チェック時は入力チェック一時保存用の値を確認
    for (const [index, item] of tempScheduleItems.entries()) {
      const errors: string[] = [];
      // 項目名
      if (!item.name) {
        // 必須チェック
        errors.push(`${FIELD_ITEM_NAME}${requiredMessage}`);
      }

      // 日付
      if (!item.date) {
        // 必須チェック
        errors.push(`${FIELD_DATE}${requiredMessage}`);
      } else if (!isValidDateFormat(item.date)) {
        // 日付フォーマット
        errors.push(`${FIELD_DATE}${isValidDateFormatMessage}`);
      } else if (!isValidDateRange(new Date(item.date))) {
        // 有効範囲
        errors.push(`${FIELD_DATE}${inputDateRangeMessage}`);
      }

      // エラーチェック結果設定
      scheduleErrorsTemp[index] = errors;
    }

    // --------------------
    // 設備投資スケジュール
    // --------------------
    if (facilityItems.length === 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Scheduling.validation.facilityRequired'),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    for (const [index, item] of tempFacilityItems.entries()) {
      const errors: string[] = [];
      // 項目名
      if (!item.name) {
        // 必須
        errors.push(`${FIELD_ITEM_NAME}${requiredMessage}`);
      }

      // 開始日
      if (!item.startDate) {
        // 必須
        errors.push(`${FIELD_START_DATE}${requiredMessage}`);
      } else if (!isValidDateFormat(item.startDate)) {
        // 日付フォーマット
        errors.push(`${FIELD_START_DATE}${isValidDateFormatMessage}`);
      } else if (!isValidDateRange(new Date(item.startDate))) {
        // 有効範囲
        errors.push(`${FIELD_START_DATE}${inputDateRangeMessage}`);
      }

      // 終了日
      if (!item.endDate) {
        // 必須
        errors.push(`${FIELD_END_DATE}${requiredMessage}`);
      } else if (!isValidDateFormat(item.endDate)) {
        // 日付フォーマット
        errors.push(`${FIELD_END_DATE}${isValidDateFormatMessage}`);
      } else if (!isValidDateRange(new Date(item.endDate))) {
        // 有効範囲
        errors.push(`${FIELD_END_DATE}${inputDateRangeMessage}`);
      }

      if (item.startDate && item.endDate && new Date(item.startDate) > new Date(item.endDate)) {
        errors.push(t('features.InvestmentScreening.Scheduling.validation.startAfterEnd'));
      }

      facilityErrorsTemp[index] = errors;
    }

    // --------------------
    // 振り返りスケジュール
    // --------------------
    const reviewErrorsTemp: string[][] = [];

    // 振り返りスケジュールが1つ以上登録されているかチェック
    if (tempReviewItems.length === 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Scheduling.validation.reviewRequired'),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    // 量産開始日をプロジェクトデータから取得
    const sopDate = projectData?.projectStartDate && isValidDateFormat(projectData.projectStartDate)
      ? new Date(projectData.projectStartDate) : null;

    // 計上期間終了日を取得（設備の実際のEOL予定日を表す新規eolDateフィールドとは異なる）
    const projectEndDate = projectData?.projectEndDate && isValidDateFormat(projectData.projectEndDate)
      ? new Date(projectData.projectEndDate) : null;

    const FIELD_PHASE = FIELD_PHASE_LABEL;

    for (const [index, item] of tempReviewItems.entries()) {
      const errors: string[] = [];

      // フェーズ必須チェック
      if (!item.phase) {
        errors.push(`${FIELD_PHASE}${requiredMessage}`);
      }

      // 日付必須・フォーマット・範囲チェック
      if (!item.date) {
        errors.push(`${FIELD_DATE}${requiredMessage}`);
      } else if (!isValidDateFormat(item.date)) {
        errors.push(`${FIELD_DATE}${isValidDateFormatMessage}`);
      } else if (!isValidDateRange(new Date(item.date))) {
        errors.push(`${FIELD_DATE}${inputDateRangeMessage}`);
      } else if (sopDate && new Date(item.date) <= sopDate) {
        errors.push(t('features.InvestmentScreening.Scheduling.validation.reviewAfterSop', { startDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectStartDate') }));
      } else if (projectEndDate && new Date(item.date) > projectEndDate) {
        errors.push(t('features.InvestmentScreening.Scheduling.validation.reviewBeforeEol', { endDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectEndDate'), endDate: formatDateByLocale(projectEndDate) }));
      } else {
        // 同一日の重複チェック
        const duplicateExists = tempReviewItems.some((other, otherIndex) =>
          otherIndex !== index && other.date === item.date
        );
        if (duplicateExists) {
          errors.push(t('features.InvestmentScreening.Scheduling.validation.reviewDuplicate'));
        }
      }

      reviewErrorsTemp[index] = errors;
    }

    setScheduleErrors(scheduleErrorsTemp);
    setFacilityErrors(facilityErrorsTemp);
    setReviewErrors(reviewErrorsTemp);

    const hasScheduleErrors = scheduleErrorsTemp.some((errors) => errors.length > 0);
    const hasFacilityErrors = facilityErrorsTemp.some((errors) => errors.length > 0);
    const hasReviewErrors = reviewErrorsTemp.some((errors) => errors.length > 0);

    if (hasScheduleErrors || hasFacilityErrors || hasReviewErrors) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.Scheduling.validation.hasErrors'),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  // -----------------------------
  // モーダル
  // -----------------------------
  // モーダルを開く
  const openModal = () => {
    onOpen();
  };

  // -----------------------------
  // 画面レイアウト
  // -----------------------------
  // 年・月ごとにまとめる
  const grouped = groupByYear(monthList);

  /** スケジュールメイン レイアウト */
  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
    >
      <Text mb="10px" color="blue">{t('features.InvestmentScreening.Scheduling.label.projectName')} :
        {projectData?.revisedOpportunityNameByRevision ? projectData.revisedOpportunityNameByRevision : projectData?.opportunityName}
      </Text>

      {/* スケジュール描画 */}
      <ScheduleTable
        scheduleItems={scheduleItems}
        facilityItems={facilityItems}
        reviewItems={reviewItems}
        monthList={monthList}
        grouped={grouped}
        showTitle={true}
        isReorderEnabled={true}
        scheduleReorder={scheduleReorder}
        facilityReorder={facilityReorder}
      />

      {/* 顧客スケジュール アコーディオン */}
      <Accordion allowToggle defaultIndex={[]}>
        <AccordionItem>
          <AccordionButton
            boxShadow="md"
            p={4}
            bg="white"
            mb={2}
            borderRadius="md"
          >
            <Box flex="1" textAlign="left" fontSize="18px" color="gray.600" fontWeight="bold">
              {t('features.InvestmentScreening.Scheduling.section.customerSchedule')}
            </Box>
            <AccordionIcon />
          </AccordionButton>

          <AccordionPanel boxShadow="md" p={4} bg="white" mb={8} borderRadius="md">
            <fieldset disabled={!pagePermissions?.isEditable}>
              {/* 並び替えモード中の案内表示 */}
              {scheduleReorder.isReorderMode && (
                <Box bg="orange.50" p={2} mb={4} borderRadius="md" borderWidth="1px" borderColor="orange.200">
                  <Text color="orange.600" fontSize="sm">
                    {t('features.InvestmentScreening.Scheduling.label.reorderModeText')}
                  </Text>
                </Box>
              )}

              {tempScheduleItems.map((item, index) => (
                <HStack
                  key={index}
                  mb="10px"
                  spacing="10px"
                  alignItems="flex-start"
                  draggable={scheduleReorder.isReorderMode}
                  onDragStart={() => scheduleReorder.dragStart(index)}
                  onDragOver={scheduleReorder.dragOver}
                  onDrop={() => scheduleReorder.drop(index)}
                  onDragEnd={scheduleReorder.dragEnd}
                  bg={scheduleReorder.draggedIndex === index ? "blue.50" : "transparent"}
                  p={scheduleReorder.isReorderMode ? 2 : 0}
                  borderRadius="md"
                  cursor={scheduleReorder.isReorderMode ? "grab" : "default"}
                  borderWidth={scheduleReorder.isReorderMode ? "1px" : "0"}
                  borderColor={scheduleReorder.isReorderMode ? "gray.200" : "transparent"}
                  _hover={scheduleReorder.isReorderMode ? { borderColor: "blue.300" } : {}}
                >
                  {/* 並び替えモード時のみ表示：順序変更ボタン */}
                  {scheduleReorder.isReorderMode && (
                    <VStack spacing={1}>
                      <Button
                        size="xs"
                        onClick={() => scheduleReorder.moveUp(index)}
                        isDisabled={index === 0}
                        variant="outline"
                      >
                        ▲
                      </Button>
                      <Button
                        size="xs"
                        onClick={() => scheduleReorder.moveDown(index)}
                        isDisabled={index === scheduleItems.length - 1}
                        variant="outline"
                      >
                        ▼
                      </Button>
                    </VStack>
                  )}

                  {/* 営業支援ラベル */}
                  {scheduleItems[index]?.source === 'sas' && (
                    <Badge colorScheme="green" fontSize="xs" px={2} py={1} borderRadius="md" alignSelf="center">
                      {t('features.InvestmentScreening.Scheduling.badge.sas')}
                    </Badge>
                  )}
                  {/* 設備投資ラベル */}
                  {scheduleItems[index]?.source !== 'sas' && (
                    <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md" alignSelf="center">
                      {t('features.InvestmentScreening.Scheduling.badge.equipment')}
                    </Badge>
                  )}

                  <VStack align="start" width={scheduleReorder.isReorderMode ? "35%" : "40%"}>
                    <FormControl isInvalid={scheduleErrors[index]?.some((error) => error.includes(FIELD_ITEM_NAME_LABEL))}>
                      <HStack spacing={1}>
                        <Button
                          size="xs"
                          variant={scheduleInputModes[index] === "select" ? "solid" : "outline"}
                          colorScheme="blue"
                          onClick={() => { if (scheduleInputModes[index] !== "select") toggleScheduleInputMode(index, "select"); }}
                          minW="40px"
                        >
                          {t('features.InvestmentScreening.Scheduling.button.select')}
                        </Button>
                        <Button
                          size="xs"
                          variant={scheduleInputModes[index] === "input" ? "solid" : "outline"}
                          colorScheme="blue"
                          onClick={() => { if (scheduleInputModes[index] !== "input") toggleScheduleInputMode(index, "input"); }}
                          minW="40px"
                        >
                          {t('features.InvestmentScreening.Scheduling.button.input')}
                        </Button>
                        {scheduleInputModes[index] === "select" ? (
                          <Select
                            placeholder={t('common.placeholder.selectLabel')}
                            value={item.name}
                            onChange={(e) => handleScheduleItemSelectChange(index, e.target.value)}
                            flex={1}
                            size="md"
                          >
                            {CustomerSchedulePresets.map((preset) => (
                              <option key={preset.value} value={preset.value}>{preset.label}</option>
                            ))}
                          </Select>
                        ) : (
                          <Input
                            placeholder={t('features.InvestmentScreening.Scheduling.placeholder.itemInput')}
                            value={item.name}
                            onChange={(e) => handleScheduleItemNameChange(index, e.target.value)}
                            onBlur={() => handleScheduleItemNameBlur(index)}
                            maxLength={50}
                            flex={1}
                          />
                        )}
                      </HStack>
                      <FormErrorMessage>
                        {scheduleErrors[index]?.filter((error) => error.includes(FIELD_ITEM_NAME_LABEL)).join(" ")}
                      </FormErrorMessage>
                    </FormControl>
                  </VStack>
                  <VStack align="start" width={scheduleReorder.isReorderMode ? "35%" : "40%"}>
                    <FormControl isInvalid={scheduleErrors[index]?.some((error) => error.includes(FIELD_DATE_LABEL))}>
                      <Input
                        type="date"
                        value={item.date}
                        onChange={(e) => handleScheduleItemChange(index, e.target.value)}
                        onBlur={() => handleScheduleItemDateBlur(index)}
                      />
                      <FormErrorMessage>
                        {scheduleErrors[index]?.filter((error) => error.includes(FIELD_DATE_LABEL)).join(" ")}
                      </FormErrorMessage>
                    </FormControl>
                  </VStack>
                  {/* 削除ボタン：並び替えモード中は非表示 */}
                  {!scheduleReorder.isReorderMode && (
                    <Button
                      colorScheme="red"
                      onClick={() => handleRemoveScheduleItem(index)}
                      leftIcon={<FaCircleMinus />}
                      alignSelf="flex-start"
                    >
                      {t('features.InvestmentScreening.Scheduling.button.delete')}
                    </Button>
                  )}
                </HStack>
              ))}
              <HStack spacing={4}>
                <Button
                  colorScheme="teal"
                  onClick={handleAddScheduleItem}
                  leftIcon={<FaPlusCircle />}
                >
                  {t('features.InvestmentScreening.Scheduling.button.addSchedule')}
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleFetchSasSchedule}
                  isLoading={isFetchingSasSchedule}
                  loadingText={t('features.InvestmentScreening.Scheduling.button.fetchingText')}
                  isDisabled={!pagePermissions?.isEditable || !projectData?.opportunityId}
                >
                  {t('features.InvestmentScreening.Scheduling.button.fetchFromSas')}
                </Button>
                <Button
                  colorScheme={scheduleReorder.isReorderMode ? "orange" : "gray"}
                  onClick={scheduleReorder.toggleReorderMode}
                  isDisabled={scheduleItems.length <= 1 || scheduleReorder.isProcessing}
                  size="md"
                >
                  {scheduleReorder.isReorderMode
                    ? t('features.InvestmentScreening.Scheduling.button.reorderComplete')
                    : t('features.InvestmentScreening.Scheduling.button.reorder')}
                </Button>
              </HStack>

              {/* 営業情報 */}
              <Box mt={6} p={4} bg="white" boxShadow="sm" borderRadius="md">
                <Text mt="5px" fontSize="md" fontWeight="bold">
                  {t('features.InvestmentScreening.Scheduling.section.salesInfo')}
                </Text>
                <Text mt="5px">
                  {t('features.InvestmentScreening.Scheduling.label.investmentAcceptanceDate')}
                  {projectData && projectData.projectStartDate ? formatDateByLocale(projectData.projectStartDate) : "-"}
                </Text>
                <Text mt="5px">
                  {t('features.InvestmentScreening.Scheduling.label.customerProjectStartDate')}
                  {customerProductData && customerProductData.projectStart ? formatDateByLocale(customerProductData.projectStart) : "-"}
                </Text>
                <Text mt="5px">
                  {t('features.InvestmentScreening.Scheduling.label.massProductionStartDate')}
                  {customerProductData && customerProductData.massProduction ? formatDateByLocale(customerProductData.massProduction) : "-"}
                </Text>
              </Box>
            </fieldset>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {/* 設備投資スケジュール アコーディオン */}
      <Accordion allowToggle defaultIndex={[]}>
        <AccordionItem>
          <AccordionButton
            boxShadow="md"
            p={4}
            bg="white"
            mb={2}
            borderRadius="md"
          >
            <Box flex="1" textAlign="left" fontSize="18px" color="gray.600" fontWeight="bold">
              {t('features.InvestmentScreening.Scheduling.section.facilitySchedule')}
            </Box>
            <AccordionIcon />
          </AccordionButton>

          <AccordionPanel boxShadow="md" p={4} bg="white" mb={8} borderRadius="md">
            <fieldset disabled={!pagePermissions?.isEditable}>
              {/* 並び替えモード中の案内表示 */}
              {facilityReorder.isReorderMode && (
                <Box bg="orange.50" p={2} mb={4} borderRadius="md" borderWidth="1px" borderColor="orange.200">
                  <Text color="orange.600" fontSize="sm">
                    {t('features.InvestmentScreening.Scheduling.label.reorderModeText')}
                  </Text>
                </Box>
              )}

              {tempFacilityItems.map((fi, index) => (
                <HStack
                  key={index}
                  mb="10px"
                  spacing="10px"
                  alignItems="flex-start"
                  draggable={facilityReorder.isReorderMode}
                  onDragStart={() => facilityReorder.dragStart(index)}
                  onDragOver={facilityReorder.dragOver}
                  onDrop={() => facilityReorder.drop(index)}
                  onDragEnd={facilityReorder.dragEnd}
                  bg={facilityReorder.draggedIndex === index ? "blue.50" : "transparent"}
                  p={facilityReorder.isReorderMode ? 2 : 0}
                  borderRadius="md"
                  cursor={facilityReorder.isReorderMode ? "grab" : "default"}
                  borderWidth={facilityReorder.isReorderMode ? "1px" : "0"}
                  borderColor={facilityReorder.isReorderMode ? "gray.200" : "transparent"}
                  _hover={facilityReorder.isReorderMode ? { borderColor: "blue.300" } : {}}
                >
                  {/* 並び替えモード時のみ表示：順序変更ボタン */}
                  {facilityReorder.isReorderMode && (
                    <VStack spacing={1}>
                      <Button
                        size="xs"
                        onClick={() => facilityReorder.moveUp(index)}
                        isDisabled={index === 0}
                        variant="outline"
                      >
                        ▲
                      </Button>
                      <Button
                        size="xs"
                        onClick={() => facilityReorder.moveDown(index)}
                        isDisabled={index === facilityItems.length - 1}
                        variant="outline"
                      >
                        ▼
                      </Button>
                    </VStack>
                  )}

                  <VStack align="start" width={facilityReorder.isReorderMode ? "25%" : "30%"}>
                    <FormControl isInvalid={facilityErrors[index]?.some((error) => error.includes(FIELD_ITEM_NAME_LABEL))}>
                      <HStack spacing={1}>
                        <Button
                          size="xs"
                          variant={facilityInputModes[index] === "select" ? "solid" : "outline"}
                          colorScheme="blue"
                          onClick={() => { if (facilityInputModes[index] !== "select") toggleFacilityInputMode(index, "select"); }}
                          minW="40px"
                        >
                          {t('features.InvestmentScreening.Scheduling.button.select')}
                        </Button>
                        <Button
                          size="xs"
                          variant={facilityInputModes[index] === "input" ? "solid" : "outline"}
                          colorScheme="blue"
                          onClick={() => { if (facilityInputModes[index] !== "input") toggleFacilityInputMode(index, "input"); }}
                          minW="40px"
                        >
                          {t('features.InvestmentScreening.Scheduling.button.input')}
                        </Button>
                        {facilityInputModes[index] === "select" ? (
                          <Select
                            placeholder={t('common.placeholder.selectLabel')}
                            value={fi.name}
                            onChange={(e) => handleFacilityItemSelectChange(index, e.target.value)}
                            flex={1}
                            size="md"
                          >
                            {FacilityItemPresets.map((preset) => (
                              <option key={preset.value} value={preset.value}>{preset.label}</option>
                            ))}
                          </Select>
                        ) : (
                          <Input
                            placeholder={t('features.InvestmentScreening.Scheduling.placeholder.facilityItemInput')}
                            value={fi.name}
                            onChange={(e) => handleFacilityItemNameChange(index, e.target.value)}
                            onBlur={() => handleFacilityItemNameBlur(index)}
                            maxLength={50}
                            flex={1}
                          />
                        )}
                      </HStack>
                      <FormErrorMessage>
                        {facilityErrors[index]?.filter((error) => error.includes(FIELD_ITEM_NAME_LABEL)).join(" ")}
                      </FormErrorMessage>
                    </FormControl>
                  </VStack>
                  <VStack align="start" width={facilityReorder.isReorderMode ? "25%" : "30%"}>
                    <FormControl isInvalid={facilityErrors[index]?.some((error) => error.includes(FIELD_START_DATE_LABEL))}>
                      <Input
                        type="date"
                        placeholder={FIELD_START_DATE_LABEL}
                        value={fi.startDate}
                        onChange={(e) => handleFacilityItemDateChange(index, "startDate", e.target.value)}
                        onBlur={() => handleFacilityItemDateBlur(index, "startDate")}
                      />
                      <FormErrorMessage>
                        {facilityErrors[index]?.filter((error) => error.includes(FIELD_START_DATE_LABEL)).join(" ")}
                      </FormErrorMessage>
                    </FormControl>
                  </VStack>
                  <VStack align="start" width={facilityReorder.isReorderMode ? "25%" : "30%"}>
                    <FormControl isInvalid={facilityErrors[index]?.some((error) => error.includes(FIELD_END_DATE_LABEL))}>
                      <Input
                        type="date"
                        placeholder={FIELD_END_DATE_LABEL}
                        value={fi.endDate}
                        onChange={(e) => handleFacilityItemDateChange(index, "endDate", e.target.value)}
                        onBlur={() => handleFacilityItemDateBlur(index, "endDate")}
                      />
                      <FormErrorMessage>
                        {facilityErrors[index]?.filter((error) => error.includes(FIELD_END_DATE_LABEL)).join(" ")}
                      </FormErrorMessage>
                    </FormControl>
                  </VStack>
                  {/* 削除ボタン：並び替えモード中は非表示 */}
                  {!facilityReorder.isReorderMode && (
                    <Button
                      colorScheme="red"
                      onClick={() => handleRemoveFacilityItem(index)}
                      leftIcon={<FaCircleMinus />}
                      alignSelf="flex-start"
                    >
                      {t('features.InvestmentScreening.Scheduling.button.delete')}
                    </Button>
                  )}
                </HStack>
              ))}
              <HStack spacing={4}>
                <Button
                  colorScheme="teal"
                  onClick={handleAddFacilityItem}
                  leftIcon={<FaPlusCircle />}
                >
                  {t('features.InvestmentScreening.Scheduling.button.addSchedule')}
                </Button>
                <Button
                  colorScheme={facilityReorder.isReorderMode ? "orange" : "gray"}
                  onClick={facilityReorder.toggleReorderMode}
                  isDisabled={facilityItems.length <= 1 || facilityReorder.isProcessing}
                  size="md"
                >
                  {facilityReorder.isReorderMode
                    ? t('features.InvestmentScreening.Scheduling.button.reorderComplete')
                    : t('features.InvestmentScreening.Scheduling.button.reorder')}
                </Button>
              </HStack>
            </fieldset>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      {/* 振り返りスケジュール アコーディオン */}
      <Accordion allowToggle defaultIndex={[]}>
        <AccordionItem>
          <AccordionButton
            boxShadow="md"
            p={4}
            bg="white"
            mb={2}
            borderRadius="md"
          >
            <Box flex="1" textAlign="left" fontSize="18px" color="gray.600" fontWeight="bold">
              {t('features.InvestmentScreening.Scheduling.section.reviewSchedule')}
            </Box>
            <AccordionIcon />
          </AccordionButton>

          <AccordionPanel boxShadow="md" p={4} bg="white" mb={8} borderRadius="md">
            <fieldset disabled={!pagePermissions?.isEditable}>
              {/* 並び替えモード中の案内表示 */}
              {reviewReorder.isReorderMode && (
                <Box bg="orange.50" p={2} mb={4} borderRadius="md" borderWidth="1px" borderColor="orange.200">
                  <Text color="orange.600" fontSize="sm">
                    {t('features.InvestmentScreening.Scheduling.label.reorderModeText')}
                  </Text>
                </Box>
              )}

              {tempReviewItems.map((item, index) => (
                <HStack
                  key={index}
                  mb="10px"
                  spacing="10px"
                  alignItems="flex-start"
                  draggable={reviewReorder.isReorderMode}
                  onDragStart={() => reviewReorder.dragStart(index)}
                  onDragOver={reviewReorder.dragOver}
                  onDrop={() => reviewReorder.drop(index)}
                  onDragEnd={reviewReorder.dragEnd}
                  bg={reviewReorder.draggedIndex === index ? "blue.50" : "transparent"}
                  p={reviewReorder.isReorderMode ? 2 : 0}
                  borderRadius="md"
                  cursor={reviewReorder.isReorderMode ? "grab" : "default"}
                  borderWidth={reviewReorder.isReorderMode ? "1px" : "0"}
                  borderColor={reviewReorder.isReorderMode ? "gray.200" : "transparent"}
                  _hover={reviewReorder.isReorderMode ? { borderColor: "blue.300" } : {}}
                >
                  {/* 並び替えモード時のみ表示：順序変更ボタン */}
                  {reviewReorder.isReorderMode && (
                    <VStack spacing={1}>
                      <Button
                        size="xs"
                        onClick={() => reviewReorder.moveUp(index)}
                        isDisabled={index === 0}
                        variant="outline"
                      >
                        ▲
                      </Button>
                      <Button
                        size="xs"
                        onClick={() => reviewReorder.moveDown(index)}
                        isDisabled={index === reviewItems.length - 1}
                        variant="outline"
                      >
                        ▼
                      </Button>
                    </VStack>
                  )}

                  <VStack align="start" width={reviewReorder.isReorderMode ? "35%" : "40%"}>
                    <FormControl isInvalid={reviewErrors[index]?.some((error) => error.includes(FIELD_PHASE_LABEL))}>
                      <HStack spacing={1}>
                        <Button
                          size="xs"
                          variant={reviewInputModes[index] === "select" ? "solid" : "outline"}
                          colorScheme="blue"
                          onClick={() => { if (reviewInputModes[index] !== "select") toggleReviewInputMode(index, "select"); }}
                          minW="40px"
                          isDisabled={isReviewedItem(index)}
                        >
                          {t('features.InvestmentScreening.Scheduling.button.select')}
                        </Button>
                        <Button
                          size="xs"
                          variant={reviewInputModes[index] === "input" ? "solid" : "outline"}
                          colorScheme="blue"
                          onClick={() => { if (reviewInputModes[index] !== "input") toggleReviewInputMode(index, "input"); }}
                          minW="40px"
                          isDisabled={isReviewedItem(index)}
                        >
                          {t('features.InvestmentScreening.Scheduling.button.input')}
                        </Button>
                        {reviewInputModes[index] === "select" ? (
                          <Select
                            placeholder={t('features.InvestmentScreening.Scheduling.placeholder.phaseSelect')}
                            value={item.phase}
                            onChange={(e) => handleReviewPhaseSelectChange(index, e.target.value)}
                            flex={1}
                            size="md"
                            isDisabled={isReviewedItem(index)}
                          >
                            {ReviewPhasePresets.map((preset) => (
                              <option key={preset.value} value={preset.value}>{preset.label}</option>
                            ))}
                          </Select>
                        ) : (
                          <Input
                            placeholder={t('features.InvestmentScreening.Scheduling.placeholder.phaseInput')}
                            value={item.phase}
                            onChange={(e) => handleReviewPhaseInputChange(index, e.target.value)}
                            onBlur={() => handleReviewPhaseInputBlur(index)}
                            maxLength={50}
                            flex={1}
                            isDisabled={isReviewedItem(index)}
                          />
                        )}
                      </HStack>
                      <FormErrorMessage>
                        {reviewErrors[index]?.filter((error) => error.includes(FIELD_PHASE_LABEL)).join(" ")}
                      </FormErrorMessage>
                    </FormControl>
                  </VStack>
                  <VStack align="start" width={reviewReorder.isReorderMode ? "35%" : "40%"}>
                    <FormControl isInvalid={reviewErrors[index]?.some((error) => error.includes(FIELD_DATE_LABEL) || error.includes(FIELD_REVIEW_DATE_LABEL))}>
                      <Input
                        type="date"
                        placeholder={t('features.InvestmentScreening.Scheduling.placeholder.reviewDate')}
                        value={item.date}
                        onChange={(e) => handleReviewDateChange(index, e.target.value)}
                        onBlur={() => handleReviewDateBlur(index)}
                        isDisabled={isReviewedItem(index)}
                      />
                      <FormErrorMessage>
                        {reviewErrors[index]?.filter((error) => error.includes(FIELD_DATE_LABEL) || error.includes(FIELD_REVIEW_DATE_LABEL)).join(" ")}
                      </FormErrorMessage>
                    </FormControl>
                  </VStack>
                  {/* 削除ボタン：並び替えモード中は非表示 */}
                  {!reviewReorder.isReorderMode && (
                    <Button
                      colorScheme="red"
                      onClick={() => handleRemoveReviewItem(index)}
                      leftIcon={<FaCircleMinus />}
                      alignSelf="flex-start"
                      isDisabled={isReviewedItem(index)}
                    >
                      {t('features.InvestmentScreening.Scheduling.button.delete')}
                    </Button>
                  )}
                </HStack>
              ))}
              <HStack spacing={4}>
                <Button
                  colorScheme="teal"
                  onClick={handleAddReviewItem}
                  leftIcon={<FaPlusCircle />}
                >
                  {t('features.InvestmentScreening.Scheduling.button.addReview')}
                </Button>
                <Button
                  colorScheme={reviewReorder.isReorderMode ? "orange" : "gray"}
                  onClick={reviewReorder.toggleReorderMode}
                  isDisabled={reviewItems.length <= 1 || reviewReorder.isProcessing}
                  size="md"
                >
                  {reviewReorder.isReorderMode
                    ? t('features.InvestmentScreening.Scheduling.button.reorderComplete')
                    : t('features.InvestmentScreening.Scheduling.button.reorder')}
                </Button>
              </HStack>
            </fieldset>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <Flex justify="flex-end" mt="20px">
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="companyDivision"
            onClick={handleDepartmentConfirm}
            isDisabled={!pagePermissions?.eabledCompanyDivision}
            ref={refs["companyDivision"]}
          >
            {t('features.InvestmentScreening.Scheduling.button.companyDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="salesDivision"
            onClick={handleDepartmentConfirm}
            isDisabled={!pagePermissions?.eabledSalesDivision}
            ref={refs["salesDivision"]}
          >
            {t('features.InvestmentScreening.Scheduling.button.salesDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["salesDivision"]} width={buttonWidths["salesDivision"]} />
        </Box>
      </Flex>

      {/* 確認モーダル */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent minWidth="30vw">
          <ModalHeader>{t('features.InvestmentScreening.Scheduling.modal.title')}</ModalHeader>
          <ModalBody maxHeight="60vh" overflowY="auto">
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem colSpan={2}>
                <Text fontWeight="bold">{t('features.InvestmentScreening.Scheduling.label.scheduleListCustomer')}</Text>
              </GridItem>
              {scheduleItems.map((item, index) => (
                <React.Fragment key={index}>
                  <GridItem>
                    <Text>{CustomerSchedulePresets.find(p => p.value === item.name)?.label || item.name}</Text>
                  </GridItem>
                  <GridItem>
                    <Text>{formatDateByLocale(item.date)}</Text>
                  </GridItem>
                </React.Fragment>
              ))}
              <GridItem colSpan={2}>
                <Text fontWeight="bold">{t('features.InvestmentScreening.Scheduling.label.scheduleListFacility')}</Text>
              </GridItem>
              {facilityItems.map((item, index) => (
                <React.Fragment key={index}>
                  <GridItem>
                    <Text>{FacilityItemPresets.find(p => p.value === item.name)?.label || item.name}</Text>
                  </GridItem>
                  <GridItem>
                    <Text>{formatDateByLocale(item.startDate)} ～ {formatDateByLocale(item.endDate)}</Text>
                  </GridItem>
                </React.Fragment>
              ))}
              {reviewItems.length > 0 && (
                <>
                  <GridItem colSpan={2}>
                    <Text fontWeight="bold">{t('features.InvestmentScreening.Scheduling.label.scheduleListReview')}</Text>
                  </GridItem>
                  {reviewItems.map((item, index) => (
                    <React.Fragment key={index}>
                      <GridItem>
                        <Text>{ReviewPhasePresets.find(p => p.value === item.phase)?.label || item.phase}</Text>
                      </GridItem>
                      <GridItem>
                        <Text>{formatDateByLocale(item.date)}</Text>
                      </GridItem>
                    </React.Fragment>
                  ))}
                </>
              )}
            </Grid>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={(e) =>
                handleConfirm(e)
              }
            >
              {t('features.InvestmentScreening.Scheduling.button.confirmAction')}
            </Button>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

export default Scheduling;
